import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import {
  Brain, RefreshCw, Check, X, Loader2, TrendingUp, Target, Gauge,
  Lightbulb, ChevronDown, ChevronUp, CircleDollarSign, AlertTriangle,
  ClipboardList, Square, CheckSquare, Package, UserRound,
} from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────

interface Proposal {
  id: number
  run_id: number | null
  created_at: string
  domain: string | null
  title: string
  hypothesis: string | null
  rationale: string | null
  expected_impact_text: string | null
  expected_metric: string | null
  baseline_value: number | null
  expected_value: number | null
  impact_eur: number | null
  confidence: number
  risk: string
  reversible: boolean
  evidence: any
  actions: any
  status: string
  reviewer_note: string | null
  belief_slug: string | null
  evaluate_after: string | null
  execution_log: string | null
  execution_mode?: string
  items_progress?: { total: number; done: number; skipped: number }
}

interface ProposalItem {
  id: number
  item_type: string
  supplier_name: string | null
  supplier_reference: string | null
  part_number: string | null
  client_code: string | null
  label: string | null
  detail: string | null
  status: string
  note: string | null
}

interface Belief {
  id: number
  slug: string
  domain: string | null
  statement: string
  confidence: number
  status: string
  updated_at: string
}

interface Dashboard {
  proposals_by_status: Record<string, number>
  decisions_scored: number
  decisions_hits: number
  hit_rate: number | null
  pipeline_impact_eur: number
  predicted_impact_eur: number
  realized_impact_eur: number
  beliefs_by_status: Record<string, number>
  domain_pipeline: { domain: string | null; n: number; impact_eur: number }[]
  last_run: { id: number; started_at: string; summary: string | null } | null
}

// ── Helpers ───────────────────────────────────────────────────

const fmtEur = (v: number | null | undefined) =>
  (v ?? 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

const DOMAIN_LABELS: Record<string, string> = {
  A_catalog: 'A · Cobertura de catálogo',
  B_demand: 'B · Cobertura de demanda',
  C_stockout: 'C · Ventas perdidas por stock',
  D_margin: 'D · Precio / margen',
  E_conversion: 'E · Conversión',
  F_channel: 'F · Mix de canal / ROI',
  G_ltv: 'G · Recompra / LTV',
  H_crosssell: 'H · Cross-sell / bundles',
}

const RISK_BADGE: Record<string, string> = {
  low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  med: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  high: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
}

const BELIEF_BADGE: Record<string, string> = {
  validated: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  hypothesis: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  refuted: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
}

const STATUS_GROUPS: { key: string; label: string; statuses: string[] }[] = [
  { key: 'new', label: 'Propuestas nuevas', statuses: ['proposed'] },
  { key: 'accepted', label: 'Aceptadas en curso', statuses: ['accepted', 'in_progress'] },
  { key: 'done', label: 'Completadas / puntuadas', statuses: ['done', 'failed', 'scored'] },
  { key: 'rejected', label: 'Rechazadas', statuses: ['rejected'] },
]

// ── Component ─────────────────────────────────────────────────

export default function BusinessBrainPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  const { data: dashboard } = useQuery<Dashboard>({
    queryKey: ['brain', 'dashboard'],
    queryFn: async () => (await api.get('/brain/dashboard')).data,
  })

  const { data: proposalsData, isLoading } = useQuery<{ proposals: Proposal[] }>({
    queryKey: ['brain', 'proposals'],
    queryFn: async () => (await api.get('/brain/proposals')).data,
  })

  const { data: beliefsData } = useQuery<{ beliefs: Belief[] }>({
    queryKey: ['brain', 'beliefs'],
    queryFn: async () => (await api.get('/brain/beliefs')).data,
  })

  const statusMutation = useMutation({
    mutationFn: async ({ id, status, note }: { id: number; status: string; note?: string }) =>
      (await api.patch(`/brain/proposals/${id}`, { status, reviewer_note: note })).data,
    onSuccess: (_d, vars) => {
      toast.success(vars.status === 'accepted' ? 'Propuesta aceptada' : 'Propuesta rechazada',
        vars.status === 'accepted' ? 'Se ejecutará en la próxima vuelta del loop.' : undefined)
      qc.invalidateQueries({ queryKey: ['brain'] })
    },
    onError: () => toast.error('No se pudo actualizar la propuesta'),
  })

  const proposals = proposalsData?.proposals ?? []
  const beliefs = beliefsData?.beliefs ?? []

  const byGroup = useMemo(() => {
    const map: Record<string, Proposal[]> = {}
    for (const g of STATUS_GROUPS) map[g.key] = []
    for (const p of proposals) {
      const g = STATUS_GROUPS.find((g) => g.statuses.includes(p.status))
      if (g) map[g.key].push(p)
    }
    return map
  }, [proposals])

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const reject = (id: number) => {
    const note = window.prompt('Motivo del rechazo (opcional):') ?? undefined
    statusMutation.mutate({ id, status: 'rejected', note })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-primary-600 to-accent-600 shadow-lg">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Cerebro de Negocio</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Propuestas para aumentar facturación y beneficio · aprende de cada decisión
            </p>
          </div>
        </div>
        <button
          onClick={() => qc.invalidateQueries({ queryKey: ['brain'] })}
          className="btn btn-secondary gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Actualizar
        </button>
      </div>

      {/* Calibración */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CircleDollarSign} label="€ en cartera (aceptadas)"
          value={fmtEur(dashboard?.pipeline_impact_eur)} tint="primary" />
        <StatCard icon={Gauge} label="Acierto del cerebro"
          value={dashboard?.hit_rate != null ? `${Math.round(dashboard.hit_rate * 100)}%` : '—'}
          sub={`${dashboard?.decisions_hits ?? 0}/${dashboard?.decisions_scored ?? 0} decisiones`} tint="emerald" />
        <StatCard icon={TrendingUp} label="€ realizado (medido)"
          value={fmtEur(dashboard?.realized_impact_eur)}
          sub={`predicho ${fmtEur(dashboard?.predicted_impact_eur)}`} tint="accent" />
        <StatCard icon={Target} label="Propuestas nuevas"
          value={String(dashboard?.proposals_by_status?.proposed ?? 0)} tint="amber" />
      </div>

      {/* € por palanca */}
      {dashboard?.domain_pipeline && dashboard.domain_pipeline.length > 0 && (
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">€ de oportunidad por palanca</h3>
          <div className="flex flex-wrap gap-2">
            {dashboard.domain_pipeline.map((d) => (
              <span key={d.domain ?? 'none'}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700/50 text-sm">
                <span className="text-slate-600 dark:text-slate-300">{DOMAIN_LABELS[d.domain ?? ''] ?? d.domain ?? 'Sin palanca'}</span>
                <span className="font-semibold text-primary-600 dark:text-primary-400">{fmtEur(d.impact_eur)}</span>
                <span className="text-xs text-slate-400">({d.n})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Propuestas por estado */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : proposals.length === 0 ? (
        <div className="card p-10 text-center">
          <Lightbulb className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <p className="text-slate-600 dark:text-slate-300 font-medium">Aún no hay propuestas.</p>
          <p className="text-sm text-slate-400 mt-1">
            Ejecuta una vuelta del cerebro (skill <code className="text-primary-600">jvs-business-brain</code> con <code>/loop</code>) para generarlas.
          </p>
        </div>
      ) : (
        STATUS_GROUPS.map((g) => {
          const items = byGroup[g.key]
          if (!items || items.length === 0) return null
          return (
            <section key={g.key} className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {g.label} <span className="text-slate-400">({items.length})</span>
              </h2>
              <div className="space-y-3">
                {items.map((p) => (
                  <ProposalCard key={p.id} p={p} expanded={expanded.has(p.id)} onToggle={() => toggle(p.id)}
                    onAccept={() => statusMutation.mutate({ id: p.id, status: 'accepted' })}
                    onReject={() => reject(p.id)}
                    busy={statusMutation.isPending} />
                ))}
              </div>
            </section>
          )
        })
      )}

      {/* Creencias */}
      {beliefs.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Conocimiento del negocio <span className="text-slate-400">({beliefs.length} creencias)</span>
          </h2>
          <div className="card divide-y divide-slate-100 dark:divide-slate-700/50">
            {beliefs.map((b) => (
              <div key={b.id} className="flex items-start gap-3 p-3">
                <span className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${BELIEF_BADGE[b.status] ?? BELIEF_BADGE.hypothesis}`}>
                  {b.status}
                </span>
                <p className="flex-1 text-sm text-slate-700 dark:text-slate-300">{b.statement}</p>
                <span className="shrink-0 text-xs font-semibold text-slate-500 dark:text-slate-400">
                  {Math.round(b.confidence * 100)}%
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

// ── Subcomponents ─────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, tint }: {
  icon: any; label: string; value: string; sub?: string; tint: string
}) {
  const tints: Record<string, string> = {
    primary: 'text-primary-600 bg-primary-100 dark:bg-primary-900/40',
    emerald: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/40',
    accent: 'text-accent-600 bg-accent-100 dark:bg-accent-900/40',
    amber: 'text-amber-600 bg-amber-100 dark:bg-amber-900/40',
  }
  return (
    <div className="card p-4">
      <div className="flex items-center gap-3">
        <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${tints[tint]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{label}</p>
          <p className="text-xl font-bold text-slate-900 dark:text-white">{value}</p>
          {sub && <p className="text-xs text-slate-400 truncate">{sub}</p>}
        </div>
      </div>
    </div>
  )
}

function ProposalCard({ p, expanded, onToggle, onAccept, onReject, busy }: {
  p: Proposal; expanded: boolean; onToggle: () => void
  onAccept: () => void; onReject: () => void; busy: boolean
}) {
  const qc = useQueryClient()
  const [showAll, setShowAll] = useState(false)
  const isNew = p.status === 'proposed'
  const isManual = p.execution_mode === 'manual'
  const prog = p.items_progress
  const hasItems = !!prog && prog.total > 0
  // En tareas manuales el checklist se muestra siempre; en auto, al expandir
  const showChecklist = hasItems && (isManual || expanded)
  const CAP = 200

  const { data: itemsData } = useQuery<{ items: ProposalItem[] }>({
    queryKey: ['brain', 'items', p.id],
    queryFn: async () => (await api.get(`/brain/proposals/${p.id}/items`)).data,
    enabled: showChecklist,
  })

  const itemMutation = useMutation({
    mutationFn: async ({ itemId, status }: { itemId: number; status: string }) =>
      (await api.patch(`/brain/proposals/items/${itemId}`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brain'] }),
  })

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {p.domain && (
              <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                {DOMAIN_LABELS[p.domain] ?? p.domain}
              </span>
            )}
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${RISK_BADGE[p.risk] ?? RISK_BADGE.low}`}>
              riesgo {p.risk}
            </span>
            {isManual && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                <ClipboardList className="w-3 h-3" /> tarea manual
              </span>
            )}
            {!p.reversible && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                <AlertTriangle className="w-3 h-3" /> no reversible
              </span>
            )}
          </div>
          <h3 className="font-semibold text-slate-900 dark:text-white">{p.title}</h3>
          {p.expected_impact_text && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{p.expected_impact_text}</p>
          )}
          {hasItems && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                <span>{prog!.done}/{prog!.total} completadas{prog!.skipped ? ` · ${prog!.skipped} omitidas` : ''}</span>
                <span>{Math.round(100 * (prog!.done + prog!.skipped) / prog!.total)}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${100 * (prog!.done + prog!.skipped) / prog!.total}%` }} />
              </div>
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-primary-600 dark:text-primary-400">{fmtEur(p.impact_eur)}</p>
          <p className="text-xs text-slate-400">confianza {Math.round(p.confidence * 100)}%</p>
        </div>
      </div>

      {/* Checklist de ítems (propuestas manuales) */}
      {showChecklist && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/50">
          <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
            Checklist — marca cada uno al completarlo:
          </p>
          <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
            {(showAll ? (itemsData?.items ?? []) : (itemsData?.items ?? []).slice(0, CAP)).map((it) => {
              const done = it.status === 'done'
              const id = it.supplier_reference || it.part_number || it.client_code || it.label
              const Icon = it.item_type === 'client' ? UserRound : Package
              return (
                <button
                  key={it.id}
                  onClick={() => itemMutation.mutate({ itemId: it.id, status: done ? 'pending' : 'done' })}
                  disabled={itemMutation.isPending}
                  className={`w-full flex items-start gap-2 text-left p-2 rounded-lg transition-colors ${
                    done ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700/40'
                  }`}
                >
                  {done
                    ? <CheckSquare className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
                    : <Square className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />}
                  <Icon className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
                  <span className="min-w-0 flex-1">
                    <span className={`text-sm ${done ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-300'}`}>
                      <span className="font-mono font-medium">{id}</span>
                      {it.label && id !== it.label && <span className="ml-2">{it.label}</span>}
                    </span>
                    {it.detail && <span className="block text-xs text-slate-400">{it.detail}</span>}
                  </span>
                </button>
              )
            })}
          </div>
          {!showAll && (prog?.total ?? 0) > CAP && (
            <button onClick={() => setShowAll(true)}
              className="mt-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline">
              Mostrar todas ({prog!.total})
            </button>
          )}
        </div>
      )}

      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/50 space-y-2 text-sm">
          {p.hypothesis && <p><span className="font-medium text-slate-600 dark:text-slate-300">Hipótesis:</span> <span className="text-slate-600 dark:text-slate-400">{p.hypothesis}</span></p>}
          {p.rationale && <p><span className="font-medium text-slate-600 dark:text-slate-300">Razonamiento:</span> <span className="text-slate-600 dark:text-slate-400">{p.rationale}</span></p>}
          {p.actions && (
            <div>
              <span className="font-medium text-slate-600 dark:text-slate-300">Acciones a ejecutar:</span>
              <pre className="mt-1 text-xs bg-slate-50 dark:bg-slate-800 rounded p-2 overflow-x-auto text-slate-600 dark:text-slate-400">{JSON.stringify(p.actions, null, 2)}</pre>
            </div>
          )}
          {p.evidence && (
            <div>
              <span className="font-medium text-slate-600 dark:text-slate-300">Evidencia:</span>
              <pre className="mt-1 text-xs bg-slate-50 dark:bg-slate-800 rounded p-2 overflow-x-auto text-slate-600 dark:text-slate-400">{JSON.stringify(p.evidence, null, 2)}</pre>
            </div>
          )}
          {p.execution_log && <p><span className="font-medium text-slate-600 dark:text-slate-300">Ejecución:</span> <span className="text-slate-600 dark:text-slate-400">{p.execution_log}</span></p>}
          {p.evaluate_after && <p className="text-xs text-slate-400">Se medirá a partir de {p.evaluate_after}</p>}
          {p.reviewer_note && <p className="text-xs text-slate-400">Nota: {p.reviewer_note}</p>}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <button onClick={onToggle} className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 inline-flex items-center gap-1">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Menos detalle' : 'Más detalle'}
        </button>
        {isNew && (
          <div className="flex items-center gap-2">
            <button onClick={onReject} disabled={busy}
              className="btn btn-secondary btn-sm gap-1 text-red-600 dark:text-red-400">
              <X className="w-4 h-4" /> Rechazar
            </button>
            <button onClick={onAccept} disabled={busy}
              className="btn btn-primary btn-sm gap-1">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Aceptar
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
